import { useMemo } from 'react';
import LegalPageLayout from '@/components/layout/LegalPageLayout';

const tocItems = [
  { id: 'status', label: 'System Status' },
  { id: 'uptime', label: 'Uptime Metrics' },
  { id: 'incidents', label: 'Recent Incidents' },
  { id: 'services', label: 'Service Health' },
  { id: 'history', label: 'Incident History' },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="reveal py-10 border-b border-white/[0.04]">
      <h2 className="text-[22px] font-semibold tracking-tight leading-snug mb-5 text-white scroll-mt-[110px]">
        {title}
      </h2>
      <div className="text-sm text-zinc-500 leading-[1.75]">{children}</div>
    </section>
  );
}

function InfoCard({ children, accent, good, warn, bad }) {
  let border = 'border-white/[0.06]';
  let bg = 'bg-white/[0.02]';
  if (accent) { border = 'border-white/[0.12]'; bg = 'bg-white/[0.04]'; }
  if (good) { border = 'border-emerald-500/20'; bg = 'bg-emerald-500/[0.04]'; }
  if (warn) { border = 'border-yellow-500/20'; bg = 'bg-yellow-500/[0.04]'; }
  if (bad) { border = 'border-red-500/20'; bg = 'bg-red-500/[0.04]'; }
  return (
    <div className={`rounded-2xl border ${border} ${bg} p-5 my-5 transition-colors hover:border-white/[0.12]`}>
      {children}
    </div>
  );
}

function StatusDot({ status }) {
  const colors = {
    operational: 'bg-emerald-500',
    degraded: 'bg-yellow-500',
    outage: 'bg-red-500',
    maintenance: 'bg-blue-500',
  };
  const labels = {
    operational: 'Operational',
    degraded: 'Degraded',
    outage: 'Outage',
    maintenance: 'Maintenance',
  };
  return (
    <div className="flex items-center gap-2">
      <span className={`relative flex h-2.5 w-2.5 ${colors[status]}`}>
        <span className={`absolute inline-flex h-full w-full rounded-full ${colors[status]} opacity-75 animate-ping`} />
        <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${colors[status]}`} />
      </span>
      <span className="text-xs font-medium text-zinc-300">{labels[status]}</span>
    </div>
  );
}

function UptimeGrid() {
  const days = useMemo(() => {
    return Array.from({ length: 90 }, (_, i) => ({
      key: i,
      uptime: Math.random() > 0.05 ? 1 : 0.85,
    }));
  }, []);
  return (
    <div className="grid grid-cols-7 gap-1 mb-4">
      {days.map(({ key, uptime }) => (
        <div
          key={key}
          className="h-8 rounded-sm transition-all hover:opacity-80"
          style={{ backgroundColor: uptime === 1 ? 'rgba(34,197,94,0.4)' : 'rgba(234,179,8,0.4)' }}
          title={`Day ${key + 1}: ${(uptime * 100).toFixed(2)}%`}
        />
      ))}
    </div>
  );
}

export default function StatusPage() {
  return (
    <LegalPageLayout
      title="System Status"
      subtitle="Real-time status of ScheduleX services. Check uptime, ongoing incidents, and service health."
      tocItems={tocItems}
    >
      <Section id="status" title="System Status">
        <InfoCard good>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-white mb-1">All Systems Operational</p>
              <p className="text-sm text-zinc-400">All services are running normally. Last checked: {new Date().toLocaleTimeString()} UTC+2</p>
            </div>
            <div className="hidden sm:block">
              <span className="px-4 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs font-semibold text-emerald-400">
                &#10003; Healthy
              </span>
            </div>
          </div>
        </InfoCard>
        <InfoCard>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-3xl font-extrabold text-white">99.98%</div>
              <div className="text-xs text-zinc-600 mt-1 uppercase tracking-wider">This Month</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-extrabold text-white">0</div>
              <div className="text-xs text-zinc-600 mt-1 uppercase tracking-wider">Active Incidents</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-extrabold text-white">&lt;100ms</div>
              <div className="text-xs text-zinc-600 mt-1 uppercase tracking-wider">Avg Response</div>
            </div>
          </div>
        </InfoCard>
      </Section>

      <Section id="uptime" title="Uptime Metrics">
        <p className="mb-4">Historical uptime for the past 90 days.</p>
        <UptimeGrid />
        <div className="flex items-center gap-4 text-xs text-zinc-600">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-emerald-500/40" /> 100% uptime
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-yellow-500/40" /> Degraded
          </div>
        </div>
      </Section>

      <Section id="incidents" title="Recent Incidents">
        <p className="mb-4">No active incidents. Here are recent resolved issues.</p>
        <InfoCard>
          <div className="flex items-start gap-4">
            <div className="mt-1">
              <StatusDot status="operational" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <p className="font-medium text-white">Email Delivery Delays</p>
                <span className="text-[10px] text-zinc-600 uppercase tracking-wider">Resolved</span>
              </div>
              <p className="text-zinc-500 text-xs mb-2">Some confirmation emails were delayed by up to 15 minutes due to a third-party SMTP provider issue.</p>
              <div className="flex items-center gap-3 text-[11px] text-zinc-600">
                <span>Started: Jun 20, 2026 14:30</span>
                <span>Resolved: Jun 20, 2026 15:15</span>
                <span>Duration: 45 min</span>
              </div>
            </div>
          </div>
        </InfoCard>
        <InfoCard>
          <div className="flex items-start gap-4">
            <div className="mt-1">
              <StatusDot status="operational" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <p className="font-medium text-white">Calendar Sync Latency</p>
                <span className="text-[10px] text-zinc-600 uppercase tracking-wider">Resolved</span>
              </div>
              <p className="text-zinc-500 text-xs mb-2">Google Calendar sync experienced higher than normal latency. All events were eventually synced without data loss.</p>
              <div className="flex items-center gap-3 text-[11px] text-zinc-600">
                <span>Started: Jun 15, 2026 09:00</span>
                <span>Resolved: Jun 15, 2026 11:30</span>
                <span>Duration: 2h 30m</span>
              </div>
            </div>
          </div>
        </InfoCard>
      </Section>

      <Section id="services" title="Service Health">
        <p className="mb-4">Current status of individual services.</p>
        <div className="space-y-2">
          {[
            { name: 'Web Application', status: 'operational' },
            { name: 'API', status: 'operational' },
            { name: 'Booking Engine', status: 'operational' },
            { name: 'Email Notifications', status: 'operational' },
            { name: 'Calendar Sync', status: 'operational' },
            { name: 'Database', status: 'operational' },
            { name: 'Authentication', status: 'operational' },
            { name: 'Payment Processing', status: 'operational' },
          ].map(svc => (
            <div key={svc.name} className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
              <span className="text-sm text-white font-medium">{svc.name}</span>
              <StatusDot status={svc.status} />
            </div>
          ))}
        </div>
      </Section>

      <Section id="history" title="Incident History">
        <p className="mb-4">Past 90 days at a glance.</p>
        <InfoCard>
          <div className="space-y-3">
            {[
              { date: 'Jun 20, 2026', title: 'Email Delivery Delays', status: 'operational', duration: '45 min' },
              { date: 'Jun 15, 2026', title: 'Calendar Sync Latency', status: 'operational', duration: '2h 30m' },
              { date: 'May 28, 2026', title: 'Scheduled Maintenance', status: 'operational', duration: '30 min' },
              { date: 'May 12, 2026', title: 'API Rate Limiting Issue', status: 'operational', duration: '15 min' },
              { date: 'Apr 30, 2026', title: 'Database Maintenance', status: 'operational', duration: '1h' },
            ].map((inc, i) => (
              <div key={i} className="flex items-center gap-4 py-2 border-b border-white/[0.04] last:border-0">
                <div className="w-24 shrink-0 text-xs text-zinc-600 font-mono">{inc.date}</div>
                <div className="flex-1 text-sm text-white">{inc.title}</div>
                <div className="text-xs text-zinc-500">{inc.duration}</div>
                <div className="w-16 text-right"><StatusDot status={inc.status} /></div>
              </div>
            ))}
          </div>
        </InfoCard>
        <p className="mt-4 text-xs text-zinc-600">
          Subscribe to status updates via email or RSS. Contact <a href="mailto:status@schedulex.site" className="text-white hover:underline">status@schedulex.site</a> for incident reports.
        </p>
      </Section>
    </LegalPageLayout>
  );
}
